import React, { createContext, useContext, useState } from "react";
import {  ThemeType } from "./colors";

interface ThemeContextType {
  theme: ThemeType;
  setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_KEY = "drpaw-theme";
/** Key this app shipped under previously — read once so nobody's choice resets. */
const LEGACY_THEME_KEY = "faceflow-theme";

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<ThemeType>(() => {
    try {
      const saved =
        localStorage.getItem(THEME_KEY) || localStorage.getItem(LEGACY_THEME_KEY);
      return (saved as ThemeType) || "original";
    } catch {
      // Private mode or blocked site data — fall back to the default theme.
      return "original";
    }
  });

  const setTheme = (newTheme: ThemeType) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(THEME_KEY, newTheme);
      localStorage.removeItem(LEGACY_THEME_KEY);
    } catch {
      // Not being able to remember the choice must not break the toggle.
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeToggle = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeToggle must be used within a ThemeProvider");
  }
  return context;
};
